import {
  ESdChangeReason,
  IAction,
  IActionParameter,
  IDisplayItem,
  IDisplayItemComponent,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { PropertySdHelper } from "../../../../shared/utility/property-sd-helper";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { IOtherDIsNonEmptyEvents } from "./action-picker";

export interface ITriggerEventSymbolsGroupState {
  otherDIsNonEmptyEvents?: IOtherDIsNonEmptyEvents[];
}

export interface ITriggerEventSymbolsGroupProps {
  parameters: IActionParameter[];
  action: IAction;
  updateActionParameter: (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => void;
  updateActionDescriptionWithParameters: (desc: string, parameter: IActionParameter, symbol: ISymbol) => void;
}

class TriggerEventSymbolsGroup extends PureSdComponent<
  ITriggerEventSymbolsGroupProps & IWithSDProps & IWithContextProp,
  ITriggerEventSymbolsGroupState
> {
  propertySdHelper: PropertySdHelper;

  constructor(props: ITriggerEventSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      otherDIsNonEmptyEvents: undefined
    };
    this.propertySdHelper = new PropertySdHelper(this.getScreenDesignContextProviderValues);
  }

  getScreenDesignContextProviderValues = () => {
    return this.props.sd;
  };

  getTargetDiName = () => {
    let result = "";
    const targetDiIdActionParam = this.props.parameters.find((x) => x.name === "targetDiId");
    const targetDiId = targetDiIdActionParam?.symbol?.fixedValue ?? undefined;

    if (targetDiId) {
      if (targetDiId === -1) {
        result = "Screen";
      } else if (targetDiId === "-1_master") {
        result = "Master Screen";
      } else {
        const matchedDi = this.props.sd.screenDesignDictionary[parseInt(targetDiId, 10)];
        if (matchedDi) {
          result = matchedDi.friendlyName
            ? matchedDi.friendlyName
            : typeof matchedDi.item !== "string"
              ? matchedDi.item.name
                ? matchedDi.item.name
                : matchedDi.item.id
              : matchedDi.item;
        } else {
          let matchedDi: IDisplayItem | undefined;
          if (this.props.sd.masterSd) {
            matchedDi = this.props.sd.screenDesignDictionary[parseInt(targetDiId, 10)];
          }
          if (matchedDi) {
            result = matchedDi.friendlyName
              ? matchedDi.friendlyName
              : typeof matchedDi.item !== "string"
                ? matchedDi.item.name
                  ? matchedDi.item.name
                  : matchedDi.item.id
                : matchedDi.item;
          }
        }
      }
    }

    return result;
  };

  handleClickTargetDiName = () => {
    const targetDiIdActionParam = this.props.parameters.find((x) => x.name === "targetDiId");
    const targetDiId = targetDiIdActionParam?.symbol.fixedValue;
    if (targetDiId !== undefined) {
      this.props.sd.setSelectedDisplayItem(targetDiId);
    }
  };

  componentDidMount() {
    const targetDiName = this.getTargetDiName();
    const actionDescriptionTargetDIid = this.props.action.description?.match(/diId:(\d+)/)?.[1];
    if (actionDescriptionTargetDIid && actionDescriptionTargetDIid !== this.props.parameters[0].symbol.fixedValue) {
      let parameters = this.props.parameters;
      parameters[0].symbol.fixedValue = actionDescriptionTargetDIid;
      this.props.updateActionParameter(parameters[0], parameters[0].symbol);
    }
    if (!targetDiName) {
      let dis = this.getOtherDIsNonEmptyEvents();
      if (!dis) {
        dis = this.getMasterDIsNonEmptyEvents();
      }
      this.setState({ otherDIsNonEmptyEvents: dis });
    }
  }

  onTargetDISelect = (val: number): void => {
    const actionDescriptionTargetDIid = this.props.action.description?.match(/diId:(\d+)/)?.[1];
    let parameters = _.cloneDeep(this.props.parameters);
    let description = _.cloneDeep(this.props.action.description);
    if (val === -1) {
      parameters[0].symbol.fixedValue = -1;
    } else {
      parameters[0].symbol.fixedValue = val.toString();
    }
    if (actionDescriptionTargetDIid && actionDescriptionTargetDIid !== val.toString()) {
      const actionDescription = description?.replace(`diId:${actionDescriptionTargetDIid}`, `diId:${val.toString()}`);
      description = actionDescription;
    }
    this.props.updateActionDescriptionWithParameters(description, parameters[0], parameters[0].symbol);
  };

  getOtherDIsNonEmptyEvents = () => {
    const sd = this.props.sd.getScreenDesign();
    const result = [] as IOtherDIsNonEmptyEvents[];
    this.getOtherDIsNonEmptyEventsRecursively(sd.displayItems, result);
    return result;
  };

  getMasterDIsNonEmptyEvents = () => {
    const masterSd = this.props.sd.masterSd;
    const result = [] as IOtherDIsNonEmptyEvents[];
    if (masterSd) {
      this.getOtherDIsNonEmptyEventsRecursively(masterSd.displayItems, result);
    }
    return result;
  };

  getOtherDIsNonEmptyEventsRecursively = (
    DIs: IDisplayItem[],
    result: IOtherDIsNonEmptyEvents[]
  ): IOtherDIsNonEmptyEvents[] => {
    const selectedDisplayItem = this.getSelectedDisplayItem();
    DIs?.forEach((di) => {
      const eventNames = [] as string[];
      (di.item as IDisplayItemComponent)?.events?.forEach((evt) => {
        if (evt.actions?.length > 0) {
          eventNames.push(evt.eventName);
        }
      });
      if (eventNames.length > 0 && di.id !== selectedDisplayItem?.id) {
        result.push({
          diId: di.id,
          diFriendlyName: di.friendlyName,
          eventNames
        });
      }

      if (di?.children?.length > 0) {
        this.getOtherDIsNonEmptyEventsRecursively(di.children, result);
      }
    });

    return result;
  };

  getSelectedDisplayItem = () => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    return selectedDisplayItem;
  };

  render() {
    const targetEventNameActionParam = this.props.parameters.find((x) => x.name === "targetEventName");

    if (this.props.parameters.length <= 0) return null;

    const diOptions: KuikaSelectboxOption[] =
      this.state.otherDIsNonEmptyEvents
        ?.sort((a, b) => (a.diFriendlyName && b.diFriendlyName ? a.diFriendlyName.localeCompare(b.diFriendlyName) : 0))
        ?.map((s: IOtherDIsNonEmptyEvents): KuikaSelectboxOption => ({
          key: String(s.diId),
          value: s.diId,
          label: s.diFriendlyName === "-1" ? "Screen" : s.diFriendlyName
        })) ?? [];

    return (
      <div style={{ width: "100%" }}>
        {!this.getTargetDiName() ? (
          <>
            <div className="kuika_add_actions_properties_name">Select DI:</div>
            <div className="kuika_property-grid-column">
              <KuikaSelectbox
                searchable
                size="sm"
                style={{ width: "100%" }}
                value={this.getTargetDiName()}
                onChange={(value: number): void => this.onTargetDISelect(value)}
                className="kuika_add_actions_inputs_size"
                options={diOptions}
              />
            </div>
          </>
        ) : (
          <div>
            <span className="kuika_properties_label_text">Target DI:</span>
            <span
              style={{ float: "right", cursor: "pointer", color: "var(--accent-fg)" }}
              onClick={this.handleClickTargetDiName}
            >
              {this.getTargetDiName()}
            </span>
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          <span className="kuika_properties_label_text">Event Name:</span>
          <span className="kuika_properties_value_text" style={{ float: "right" }}>
            {targetEventNameActionParam?.symbol?.fixedValue ?? ""}
          </span>
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ActionDescriptionChanged ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const triggerEventSymbolsGroup = withContext<ITriggerEventSymbolsGroupProps>(
  withSD<ITriggerEventSymbolsGroupProps>(TriggerEventSymbolsGroup)
);
export { triggerEventSymbolsGroup as TriggerEventSymbolsGroup };
